/**
 * Game URL Mode
 * 
 * Displays a simple game state in the URL hash.
 * Level 1: Horizontal coin collection
 * Level 2: Flappy Bird style (vertical obstacles)
 */

export interface GameState {
    level: number;
    playerPosition: number;
    playerVertical: number; // For level 2 (vertical position in row)
    coins: Set<number>;
    obstacles: Array<{ x: number; y: number }>; // For level 2 (Flappy Bird style)
    score: number;
    levelLength: number;
    levelHeight: number; // For level 2 (vertical dimension)
}

/**
 * Initialize game state
 */
export function initializeGame(): GameState {
    const levelLength = 60;
    // Alternate between level 1 and 2 based on game mode cycle (every 4 minutes)
    const now = new Date();
    const minutes = now.getMinutes();
    const gameCycle = Math.floor(minutes / 12); // Each 12-minute cycle
    const level = (gameCycle % 2) + 1; // Alternate between 1 and 2

    if (level === 1) {
        // Level 1: Horizontal coin collection
        const playerPosition = Math.floor(levelLength / 2); // Start in middle

        // Generate random coin positions (about 10-15 coins)
        const coins = new Set<number>();
        const numCoins = 10 + Math.floor(Math.random() * 6);
        while (coins.size < numCoins) {
            const pos = Math.floor(Math.random() * levelLength);
            if (pos !== playerPosition) { // Don't place coin on player
                coins.add(pos);
            }
        }

        return {
            level: 1,
            playerPosition,
            playerVertical: 0,
            coins,
            obstacles: [],
            score: 0,
            levelLength,
            levelHeight: 1,
        };
    } else {
        // Level 2: Flappy Bird style (vertical obstacles)
        const levelHeight = 3; // 3 vertical positions (top, middle, bottom)
        const playerPosition = 0; // Start at left
        const playerVertical = 1; // Start in middle row

        // Generate obstacles (gaps in walls)
        const obstacles: Array<{ x: number; y: number }> = [];
        for (let x = 10; x < levelLength; x += 8) {
            const gapY = Math.floor(Math.random() * levelHeight);
            // Add obstacles on all rows except the gap
            for (let y = 0; y < levelHeight; y++) {
                if (y !== gapY) {
                    obstacles.push({ x, y });
                }
            }
        }

        return {
            level: 2,
            playerPosition,
            playerVertical,
            coins: new Set(),
            obstacles,
            score: 0,
            levelLength,
            levelHeight,
        };
    }
}

/**
 * Generate game string for URL hash
 * Level 1: "[score]#[level with player and coins]"
 * Level 2: "[score]#[row0|row1|row2]" (vertical rows separated by |)
 */
export function generateGameString(state: GameState): string {
    const { level, playerPosition, playerVertical, coins, obstacles, score, levelLength, levelHeight } = state;

    if (level === 1) {
        // Level 1: Horizontal coin collection
        const levelStr: string[] = [];

        // Build level string
        for (let i = 0; i < levelLength; i++) {
            if (i === playerPosition) {
                levelStr.push('#');
            } else if (coins.has(i)) {
                levelStr.push('$');
            } else {
                levelStr.push('.');
            }
        }

        // Format: "score#level"
        return `${score}#${levelStr.join('')}`;
    } else {
        // Level 2: Flappy Bird style (vertical obstacles)
        const rows: string[] = [];

        // Build each vertical row
        for (let y = 0; y < levelHeight; y++) {
            const row: string[] = [];
            for (let x = 0; x < levelLength; x++) {
                if (x === playerPosition && y === playerVertical) {
                    row.push('#');
                } else if (obstacles.some(obs => obs.x === x && obs.y === y)) {
                    row.push('█'); // Obstacle character
                } else {
                    row.push('.');
                }
            }
            rows.push(row.join(''));
        }

        // Format: "score#row0|row1|row2"
        return `${score}#${rows.join('|')}`;
    }
}

